import { MENU_ITEM_TYPE } from './enums';
import { hasStructureDrifted, reconcileDriftedItem } from './reconcileItemWithMenu';

/**
 * Re-prices the selections saved on a past order against the menu as it stands
 * today.
 *
 * A saved order records both what the customer chose and what those choices
 * cost at the time. A re-order has to keep the choices and drop the prices, so
 * every field the pricing functions sum - `price` on a size, on a base sub
 * modifier, on a variant, on a non base modifier's `prices` row, on an extra -
 * is restamped from the live menu item.
 *
 * Where a selection has no counterpart in the menu today its price cannot be
 * established at all. Carrying over the price stored on the order charges the
 * old price and defaulting to 0 gives the item away, so those cases report
 * UNRESOLVED and the caller sends the customer to the menu instead.
 */

export const UNRESOLVED = Symbol('unresolved');

const byId = (list, id) => (list ?? []).find((entry) => entry?._id === id);

const isChosen = (entry) => Number(entry?.quantity) > 0;

const isComplexItem = (menuItem) =>
  Number(menuItem?.itemType) === MENU_ITEM_TYPE.complex;

/**
 * A price is only usable when the menu actually carries one, and 0 is a real
 * menu price that has to survive. This is why resolution cannot lean on
 * falsiness the way `live?.price || saved.price` did - a missing live price is
 * exactly the case where that expression reached for the order's own price.
 */
const menuPrice = (entry) => {
  const price = entry?.price;
  if (price === undefined || price === null || price === '') return UNRESOLVED;
  const parsed = Number(price);
  return Number.isFinite(parsed) ? parsed : UNRESOLVED;
};

/**
 * Variants ride along with the size, sub modifier or price row that owns them.
 * One the customer never chose adds nothing, so it can be dropped when the menu
 * stops offering it; one they did choose cannot be priced without the menu.
 */
const repriceVariants = (savedVariants, liveVariants) => {
  if (!Array.isArray(savedVariants)) return savedVariants ?? null;

  const repriced = [];
  for (const savedVariant of savedVariants) {
    const liveVariant = byId(liveVariants, savedVariant._id);
    const price = menuPrice(liveVariant);
    if (price === UNRESOLVED) {
      if (isChosen(savedVariant)) return UNRESOLVED;
      continue;
    }
    repriced.push({ ...liveVariant, ...savedVariant, price: String(price) });
  }
  return repriced;
};

/**
 * `selectedSizes` prices a simple item on its own, and on a complex item it is
 * the same array the base modifier holds - addToOrder aliases the two - so both
 * pricing paths read these prices.
 */
const repriceSizes = (savedSizes, liveSizes) => {
  const repriced = [];
  for (const savedSize of savedSizes ?? []) {
    const liveSize = byId(liveSizes, savedSize._id);
    const price = menuPrice(liveSize);
    // The size the customer ordered no longer carries a price in the menu.
    if (price === UNRESOLVED) return UNRESOLVED;

    const variants = repriceVariants(savedSize.variants, liveSize.variants);
    if (variants === UNRESOLVED) return UNRESOLVED;

    repriced.push({
      ...liveSize,
      ...savedSize,
      price: String(price),
      variants,
      quantity: savedSize.quantity || 1,
    });
  }
  return repriced;
};

/**
 * A non base modifier prices itself per base sub modifier through `prices`.
 * getItemPrice reads nothing else for these groups, so this array is the whole
 * price of every modifier driven item.
 */
const repricePriceRows = (savedRows, liveRows) => {
  if (!Array.isArray(savedRows)) return savedRows ?? null;

  const repriced = [];
  for (const savedRow of savedRows) {
    const liveRow = byId(liveRows, savedRow._id);
    const price = menuPrice(liveRow);
    if (price === UNRESOLVED) {
      if (isChosen(savedRow)) return UNRESOLVED;
      continue;
    }

    const variants = repriceVariants(savedRow.variants, liveRow.variants);
    if (variants === UNRESOLVED) return UNRESOLVED;

    repriced.push({
      ...liveRow,
      ...savedRow,
      price: String(price),
      variants,
    });
  }
  return repriced;
};

const repriceModifiers = (savedModifiers, liveModifiers, baseModifierId) => {
  const repriced = [];

  for (const savedModifier of savedModifiers ?? []) {
    const liveModifier = byId(liveModifiers, savedModifier._id);
    const savedSubModifiers = savedModifier.subModifiers ?? [];
    const isBase = savedModifier._id === baseModifierId;

    if (!liveModifier) {
      // A group the menu dropped. Harmless unless the customer had picked
      // something in it, in which case the item cannot be rebuilt.
      if (isBase || savedSubModifiers.some((s) => s.isSelected)) {
        return UNRESOLVED;
      }
      continue;
    }

    const subModifiers = [];
    for (const savedSub of savedSubModifiers) {
      const liveSub = byId(liveModifier.subModifiers, savedSub._id);
      if (!liveSub) {
        if (isBase || savedSub.isSelected) return UNRESOLVED;
        continue;
      }

      if (isBase) {
        const price = menuPrice(liveSub);
        if (price === UNRESOLVED) return UNRESOLVED;

        const variants = repriceVariants(savedSub.variants, liveSub.variants);
        if (variants === UNRESOLVED) return UNRESOLVED;

        subModifiers.push({
          ...liveSub,
          ...savedSub,
          price: String(price),
          variants,
          quantity: savedSub.quantity || 1,
        });
      } else {
        const prices = repricePriceRows(savedSub.prices, liveSub.prices);
        if (prices === UNRESOLVED) return UNRESOLVED;

        subModifiers.push({
          ...liveSub,
          ...savedSub,
          // Nothing prices off a non base sub modifier's own `price`, but it
          // must not be left holding the amount the order was placed at.
          price: liveSub.price ?? null,
          prices,
          quantity: savedSub.quantity || 1,
        });
      }
    }

    // Nothing prices off a modifier group itself, so the saved group's `price`
    // and `prices` are dropped rather than restamped.
    const { price, prices, ...group } = { ...liveModifier, ...savedModifier };
    repriced.push({ ...group, subModifiers });
  }

  return repriced;
};

/**
 * An extra's price depends on the size it is going on, and on that size's
 * variant where there is one - the `finalPrices` the item view matches against
 * is just this `prices` array flattened per variant.
 */
const resolveExtraPrice = (liveExtra, sizeId, variantId) => {
  if (!liveExtra) return UNRESOLVED;

  if (Array.isArray(liveExtra.prices) && liveExtra.prices.length > 0) {
    const row = liveExtra.prices.find(
      (p) => p._id === sizeId && p.isSelected !== false
    );
    // Priced per size, but not for the size being re-ordered.
    if (!row) return UNRESOLVED;

    const price = menuPrice(row);
    if (price === UNRESOLVED) return UNRESOLVED;
    if (variantId === 'xxx') return price;

    const variant = (row.variants ?? []).find(
      (v) => v._id === variantId && v.isSelected !== false
    );
    const variantPrice = variant ? menuPrice(variant) : 0;
    if (variantPrice === UNRESOLVED) return UNRESOLVED;
    return price + variantPrice;
  }

  // Extras outside a per size structure carry a flat price.
  return menuPrice(liveExtra);
};

const repriceExtras = (savedExtras, liveExtras, sizeId, variantId) => {
  const repriced = [];

  for (const savedExtra of savedExtras ?? []) {
    const liveExtra = byId(liveExtras, savedExtra._id);
    const price = resolveExtraPrice(liveExtra, sizeId, variantId);

    if (price === UNRESOLVED) {
      // Never actually added to the item, so its price never mattered.
      if (!isChosen(savedExtra)) continue;
      return UNRESOLVED;
    }

    repriced.push({
      ...liveExtra,
      ...savedExtra,
      price: String(price),
      quantity: savedExtra.quantity || 1,
      // Keep the menu's own price structures so editing a re-ordered item in
      // the item view does not reprice it off the order again.
      ...(liveExtra.prices ? { prices: liveExtra.prices } : {}),
      ...(liveExtra.finalPrices ? { finalPrices: liveExtra.finalPrices } : {}),
    });
  }

  return repriced;
};

const chosenSize = (sizes) => {
  const list = sizes ?? [];
  return list.find(isChosen) ?? list[0];
};

const chosenVariantId = (size) =>
  (size?.variants ?? []).find(isChosen)?._id ?? 'xxx';

/**
 * @param savedItem menu item as stored on the past order
 * @param liveMenuItem the same item as it exists in the menu today, already
 *   run through getMenuItemForInstore so its prices are current
 * @returns the selections to put in the cart, priced at today's menu, or
 *   UNRESOLVED when the item cannot be priced without asking the customer
 */
const repriceReorderItem = (savedItem, liveMenuItem) => {
  const isComplex = isComplexItem(liveMenuItem);

  let selectedSizes;
  let selectedModifiers;

  // An item that was simple when it was ordered and is complex now was never
  // asked the modifier questions that price it today, so its selections have
  // to be rebuilt against the shape it has rather than restamped.
  if (hasStructureDrifted(savedItem, liveMenuItem)) {
    const reconciled = reconcileDriftedItem(savedItem, liveMenuItem);
    if (!reconciled) return UNRESOLVED;
    ({ selectedSizes, selectedModifiers } = reconciled);
  } else if (isComplex) {
    selectedModifiers = repriceModifiers(
      savedItem.selectedModifiers,
      liveMenuItem.selectedModifiers,
      liveMenuItem.baseModifierId
    );
    if (selectedModifiers === UNRESOLVED) return UNRESOLVED;

    // addToOrder keeps the base modifier's sub modifiers and selectedSizes as
    // the same objects, and the display paths read the latter.
    selectedSizes =
      selectedModifiers.find((m) => m._id === liveMenuItem.baseModifierId)
        ?.subModifiers ?? [];
  } else {
    selectedSizes = repriceSizes(
      savedItem.selectedSizes,
      liveMenuItem.selectedSizes
    );
    if (selectedSizes === UNRESOLVED) return UNRESOLVED;
  }

  const size = chosenSize(selectedSizes);
  const selectedExtraIngredients = repriceExtras(
    savedItem.selectedExtraIngredients,
    liveMenuItem.selectedExtraIngredients,
    size?._id,
    chosenVariantId(size)
  );
  if (selectedExtraIngredients === UNRESOLVED) return UNRESOLVED;

  return {
    selectedSizes,
    selectedExtraIngredients,
    // A simple item prices off selectedSizes, so the live groups are left in
    // place rather than replaced with the order's stale ones.
    ...(selectedModifiers ? { selectedModifiers } : {}),
  };
};

export default repriceReorderItem;
